import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useContentToken,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

// ============================================================================
// Slot groups
// ============================================================================

const imageDef: SlotGroupDefinition = {
  label: 'Pet Policy Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // Calm outdoor dog portrait — closed/minimal tongue (replaces the
      // prior Labrador with a large tongue lick).
      defaultValue:
        'https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=1200&q=80',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Friendly dog outdoors with a calm, happy expression',
    },
  },
  // Wire the media picker to photo_gallery so choosing a new photo also
  // updates the alt text — otherwise the picker only swaps the image and
  // the alt/caption stay on the previous value.
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Floor Plans CTA',
  fields: {
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Explore Pet Friendly Apartments',
    },
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers — currency chrome for fee/rent stats. Skips empty placeholders
// and avoids doubling when the property value already includes a leading "$".
// ============================================================================

function hasDollarPrefix(value: string | null | undefined): boolean {
  return (value ?? '').trim().startsWith('$');
}

function isEmptyStat(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim();
  return !trimmed || trimmed === '—';
}

// ============================================================================
// Component — a single dark callout card with copy on the left and a photo
// on the right. Composition: eyebrow → mixed-emphasis heading → body →
// floor-plans CTA → three-up pet policy stats.
// ============================================================================

export function PetPolicyCallout() {
  const image = useSlotGroup('petpolicycallout.image', imageDef);
  const cta = useSlotGroup('petpolicycallout.cta_link', ctaLinkDef);
  const eyebrow = useContentToken('petpolicycallout.eyebrow');
  const stat1Value = useContentToken('petpolicycallout.stat_1_value');
  const stat1Label = useContentToken('petpolicycallout.stat_1_label');
  const stat2Value = useContentToken('petpolicycallout.stat_2_value');
  const stat2Label = useContentToken('petpolicycallout.stat_2_label');
  const stat3Value = useContentToken('petpolicycallout.stat_3_value');
  const stat3Label = useContentToken('petpolicycallout.stat_3_label');

  const showDollarFee = !isEmptyStat(stat2Value) && !hasDollarPrefix(stat2Value);
  const showDollarRent = !isEmptyStat(stat3Value) && !hasDollarPrefix(stat3Value);

  useRevealStyles();
  useSiteCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up ppc-section"
    >
      <div className="ppc-inner">
        <div className="ppc-card">
          <div className="ppc-copy">
            <EditableToken
              path="petpolicycallout.eyebrow"
              as="p"
              className="ppc-eyebrow"
            >
              {eyebrow || 'Pet-Friendly Living'}
            </EditableToken>
            <RichTextContent
              path="petpolicycallout.heading"
              label="Heading"
              as="h2"
              className="ppc-heading"
            />
            <RichTextContent
              path="petpolicycallout.description"
              label="Description"
              as="div"
              className="ppc-body"
            />

            {/* CTA between copy and stats — filled surface button linking
                to floor plans (same beat as the reference composition). */}
            <div className="ppc-cta">
              <EditableSlotGroup
                groupPath="petpolicycallout.cta_link"
                definition={ctaLinkDef}
              >
                <a
                  href={cta.url || '/floor-plans'}
                  target={cta._target || '_self'}
                  className="site-cta site-cta--secondary ppc-cta-link"
                  style={{
                    // Filled surface (Aion orange) on the primary panel —
                    // reads as the solid gold CTA in the reference. Hover
                    // flips to on-primary outline so it stays visible.
                    ['--site-cta-secondary-accent' as string]:
                      'var(--vibe-color-surface, #e2903d)',
                    ['--site-cta-secondary-on-accent' as string]:
                      'var(--site-cta-ink-on-surface, #1a1a1a)',
                    ['--site-cta-hover-accent' as string]:
                      'var(--vibe-color-text-on-primary, #ffffff)',
                    ['--site-cta-hover-on-accent' as string]:
                      'var(--vibe-color-primary)',
                  }}
                >
                  {cta.label || 'Explore Pet Friendly Apartments'}
                  <span className="ppc-cta-arrow" aria-hidden="true">
                    →
                  </span>
                </a>
              </EditableSlotGroup>
            </div>

            <div className="ppc-stats" aria-label="Pet policy details">
              <div className="ppc-stats-grid">
                <div className="ppc-stat">
                  <EditableToken
                    path="petpolicycallout.stat_1_value"
                    as="p"
                    className="ppc-stat-value"
                  >
                    {stat1Value}
                  </EditableToken>
                  <EditableToken
                    path="petpolicycallout.stat_1_label"
                    as="p"
                    className="ppc-stat-label"
                  >
                    {stat1Label}
                  </EditableToken>
                </div>
                <div className="ppc-stat">
                  <p className="ppc-stat-value">
                    {showDollarFee ? <span>$</span> : null}
                    <EditableToken path="petpolicycallout.stat_2_value" as="span">
                      {stat2Value}
                    </EditableToken>
                  </p>
                  <EditableToken
                    path="petpolicycallout.stat_2_label"
                    as="p"
                    className="ppc-stat-label"
                  >
                    {stat2Label}
                  </EditableToken>
                </div>
                <div className="ppc-stat">
                  <p className="ppc-stat-value">
                    {showDollarRent ? <span>$</span> : null}
                    <EditableToken path="petpolicycallout.stat_3_value" as="span">
                      {stat3Value}
                    </EditableToken>
                  </p>
                  <EditableToken
                    path="petpolicycallout.stat_3_label"
                    as="p"
                    className="ppc-stat-label"
                  >
                    {stat3Label}
                  </EditableToken>
                </div>
              </div>
            </div>
          </div>
          <EditableSlotGroup
            groupPath="petpolicycallout.image"
            definition={imageDef}
            className="ppc-media-slot"
          >
            <div className="ppc-media">
              <VibesitesImage
                url={image.image || ''}
                alt={image.alt || 'Pet policy'}
                className="ppc-media-img"
                sizes="(min-width: 860px) 540px, 90vw"
              />
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* White band under the amenities list. */
        .ppc-section {
          background-color: #ffffff;
          padding: 5rem 1.25rem;
          /* Contain any long CTA/copy so the page never scrolls sideways. */
          overflow-x: clip;
        }
        @media (max-width: 640px) {
          .ppc-section {
            padding: 3.25rem 1rem;
          }
        }

        .ppc-inner {
          max-width: 1180px;
          margin: 0 auto;
          min-width: 0;
          width: 100%;
        }

        /* Two-panel card: copy on the left, image on the right.
           Mobile stacks image above copy via order (desktop unchanged). */
        .ppc-card {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          overflow: hidden;
          border-radius: 4px;
          min-width: 0;
          width: 100%;
        }
        @media (min-width: 860px) {
          .ppc-card {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            min-height: 480px;
          }
        }

        /* Mobile: photo first, then copy. Desktop keeps DOM order
           (copy | image). */
        @media (max-width: 859px) {
          .ppc-media-slot {
            order: 1;
            display: block;
            min-width: 0;
            max-width: 100%;
          }
          .ppc-copy {
            order: 2;
          }
        }

        /* Brand primary panel — text uses text-on-primary so contrast stays
           AA when the brand primary color changes. */
        .ppc-copy {
          background-color: var(--vibe-color-primary, #B28C59);
          color: var(--vibe-color-text-on-primary, #ffffff);
          padding: clamp(1.75rem, 5vw, 4rem);
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 0;
          min-width: 0;
          max-width: 100%;
          box-sizing: border-box;
        }
        @media (max-width: 640px) {
          .ppc-copy {
            padding: 1.75rem 1.25rem 2rem;
          }
        }

        /* Eyebrow with a short accent rule — matches the reference
           "PET-FRIENDLY LIVING" treatment. Surface is Aion's warm accent
           on the primary panel. */
        .ppc-eyebrow {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin: 0 0 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--vibe-color-surface, #e2903d);
          max-width: 100%;
        }
        .ppc-eyebrow::before {
          content: '';
          display: block;
          width: 1.5rem;
          height: 1.5px;
          background: currentColor;
          flex-shrink: 0;
        }

        .ppc-heading {
          margin: 0 0 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.6rem, 3.6vw, 2.75rem);
          line-height: 1.12;
          font-weight: 400;
          color: var(--vibe-color-text-on-primary, #ffffff);
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .ppc-heading [data-vibesite-richtext] p {
          margin: 0;
        }
        /* Accent phrase — italic + surface color so "here, too." pops
           against the on-primary heading, like the reference. */
        .ppc-heading em,
        .ppc-heading [data-vibesite-richtext] em {
          font-style: italic;
          font-weight: 400;
          color: var(--vibe-color-surface, #e2903d);
        }

        .ppc-body {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.65;
          color: var(--vibe-color-text-on-primary, #ffffff);
          max-width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .ppc-body [data-vibesite-richtext] p {
          margin: 0 0 0.85rem;
        }
        .ppc-body [data-vibesite-richtext] p:last-child {
          margin-bottom: 0;
        }
        .ppc-body [data-vibesite-richtext] a {
          color: var(--vibe-color-text-on-primary, #ffffff);
          text-decoration: underline;
          overflow-wrap: anywhere;
        }

        .ppc-cta {
          margin-top: clamp(1.5rem, 3vw, 2rem);
          min-width: 0;
          max-width: 100%;
        }
        /* EditableSlotGroup wrapper must not force the card wider than
           the viewport when the CTA label is long. */
        .ppc-cta > * {
          display: block;
          max-width: 100%;
          min-width: 0;
        }

        /* CTA — desktop/tablet share natural site-cta sizing. */
        a.site-cta.ppc-cta-link {
          box-sizing: border-box;
          width: auto;
          max-width: 100%;
          min-width: 0;
          white-space: nowrap;
          line-height: 1;
          padding: 1rem 2rem;
          letter-spacing: 0.16em;
          justify-content: flex-start;
          text-align: left;
        }

        /* Mobile only — compact, left-aligned (tablet keeps desktop size). */
        @media (max-width: 640px) {
          a.site-cta.ppc-cta-link {
            font-size: clamp(0.55rem, 2.4vw, 0.68rem);
            letter-spacing: 0.08em;
            padding: 0.7rem 1.1rem;
          }
        }

        .ppc-cta-arrow {
          display: inline-block;
          margin-left: 0.15rem;
          font-size: 1.05em;
          line-height: 1;
          transition: transform 0.2s ease;
        }
        a.site-cta.ppc-cta-link:hover .ppc-cta-arrow,
        a.site-cta.ppc-cta-link:focus-visible .ppc-cta-arrow {
          transform: translateX(3px);
        }
        @media (prefers-reduced-motion: reduce) {
          a.site-cta.ppc-cta-link:hover .ppc-cta-arrow,
          a.site-cta.ppc-cta-link:focus-visible .ppc-cta-arrow {
            transform: none;
          }
        }

        /* Stats band: hairline rule + three large values. */
        .ppc-stats {
          margin-top: clamp(1.75rem, 3.5vw, 2.5rem);
          padding-top: clamp(1.5rem, 3vw, 2rem);
          border-top: 1px solid color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 28%, transparent);
          min-width: 0;
          max-width: 100%;
        }

        .ppc-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1.25rem 1rem;
        }
        @media (min-width: 480px) {
          .ppc-stats-grid {
            gap: 1.25rem 1.75rem;
          }
        }

        .ppc-stat {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        /* Mobile: center the stats column(s). Desktop stays left-aligned. */
        @media (max-width: 859px) {
          .ppc-stat {
            align-items: center;
            text-align: center;
          }
          .ppc-stat-value {
            justify-content: center;
          }
        }

        .ppc-stat-value {
          margin: 0;
          display: flex;
          align-items: flex-end;
          font-family: var(--vibe-font-heading);
          font-size: clamp(2rem, 4.2vw, 2.85rem);
          line-height: 1;
          font-weight: 400;
          letter-spacing: -0.02em;
          color: var(--vibe-color-text-on-primary, #ffffff);
        }

        .ppc-stat-label {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.88;
          overflow-wrap: break-word;
        }

        /* Image panel: matches the copy panel's height so the two sides read
           as one balanced card. object-fit: cover keeps the pet centered even
           if the source aspect ratio doesn't match the panel exactly. */
        .ppc-media {
          position: relative;
          min-height: 280px;
          background: var(--vibe-color-surface, #d9d5cc);
          min-width: 0;
        }
        @media (min-width: 640px) {
          .ppc-media {
            min-height: 360px;
          }
        }
        @media (min-width: 860px) {
          .ppc-media {
            min-height: 100%;
          }
        }
        .ppc-media-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicyCallout',
  displayName: 'Pet Policy Callout',
  tokens: {
    'petpolicycallout.eyebrow': {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Pet-Friendly Living',
    },
    'petpolicycallout.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Your pet belongs <em>here, too.</em></p>',
    },
    'petpolicycallout.description': {
      label: 'Description',
      type: 'richtext',
      suggestedFields: [
        'property_pet_policy_description',
        'property_petpolicy_info',
        'property_pets_blurb',
      ],
      defaultValue:
        '<p>Home just wouldn&#39;t be the same without your pets, and we invite you to bring them with you to <span data-type="field-token" data-field="property_name"></span>. Questions? Contact the leasing office, or <a href="/pet-policy">review our full pet policy</a>.</p>',
    },
    'petpolicycallout.stat_1_value': {
      label: 'Stat 1 Value',
      type: 'text',
      defaultField: 'property_max_pets',
      defaultValue: '—',
    },
    'petpolicycallout.stat_1_label': {
      label: 'Stat 1 Label',
      type: 'text',
      defaultValue: 'Pets per unit',
    },
    'petpolicycallout.stat_2_value': {
      label: 'Stat 2 Value',
      type: 'text',
      defaultField: 'property_upfront_pet_fee',
      defaultValue: '—',
    },
    'petpolicycallout.stat_2_label': {
      label: 'Stat 2 Label',
      type: 'text',
      defaultValue: 'Pet fee',
    },
    'petpolicycallout.stat_3_value': {
      label: 'Stat 3 Value',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      defaultValue: '—',
    },
    'petpolicycallout.stat_3_label': {
      label: 'Stat 3 Label',
      type: 'text',
      defaultValue: 'Pet rent / mo',
    },
  },
  slotGroups: {
    'petpolicycallout.image': imageDef,
    'petpolicycallout.cta_link': ctaLinkDef,
  },
};

(PetPolicyCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicyCallout;
